import React, { useState, useEffect } from "react";
import Nav from "./Nav";
import { useNavContext } from "../context/NavContext";
import { useAuth } from "../context/AuthContext";
import Swal from "sweetalert2";
import {
  onSnapshot,
  doc,
  deleteDoc,
  addDoc,
  updateDoc,
} from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { getSubCollection } from "../utils/getSubCollection";
import GrupoModal from "./GrupoModal";
import Spinner from "./Spinner";
import { useNavigate } from "react-router-dom";
import Paginador from "./Paginador";
import Breadcrumb from "./Breadcrumb";

const Grupos = () => {
  const { user, teamOwnerId } = useAuth();
  const ownerUid = teamOwnerId || user.uid;
  const navigate = useNavigate();
  const { searchTerm, setSearchTerm, proximasAVencer, proximosEventos } =
    useNavContext();

  const [grupos, setGrupos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGrupos, setSelectedGrupos] = useState([]);
  const [grupoEditando, setGrupoEditando] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);

  // 1) Suscripción real-time a /usuarios/{ownerUid}/grupos
  useEffect(() => {
    setLoading(true);
    const gruposCol = getSubCollection(ownerUid, "grupos");
    const unsubscribe = onSnapshot(gruposCol, (snap) => {
      setGrupos(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return () => unsubscribe();
  }, [ownerUid]);

  // 2) Filtrado por término de búsqueda
  const filteredGrupos = React.useMemo(() => {
    if (!grupos.length) return [];
    const q = searchTerm.trim().toLowerCase();
    return grupos.filter((g) =>
      [g.nombre, g.descripcion]
        .map((f) => f?.toLowerCase() || "")
        .some((f) => f.includes(q))
    );
  }, [grupos, searchTerm]);

  // 3) Paginación
  const total = filteredGrupos.length;
  const start = (currentPage - 1) * itemsPerPage;
  const page = filteredGrupos.slice(start, start + itemsPerPage);

  // 4) Selección
  const handleSelectAll = (e) =>
    setSelectedGrupos(e.target.checked ? filteredGrupos.map((g) => g.id) : []);
  const handleSelectGrupo = (id) =>
    setSelectedGrupos((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  // 5) Abrir / cerrar modal (nuevo o editar)
  const openModal = (grupo = null) => {
    setGrupoEditando(grupo);
    setIsModalOpen(true);
  };
  const closeModal = () => setIsModalOpen(false);

  // 6) Crear o actualizar grupo
  const handleSubmitGrupo = async (newGrupo) => {
    const gruposCol = getSubCollection(ownerUid, "grupos");
    const isEdit = Boolean(newGrupo.id && !newGrupo.id.startsWith("temp-"));
    const tempId = isEdit ? newGrupo.id : `temp-${Date.now()}`;

    // Optimismo
    setGrupos((prev) =>
      isEdit
        ? prev.map((g) =>
            g.id === newGrupo.id
              ? {
                  ...g,
                  nombre: newGrupo.nombre,
                  descripcion: newGrupo.descripcion,
                }
              : g
          )
        : [
            ...prev,
            {
              id: tempId,
              nombre: newGrupo.nombre,
              descripcion: newGrupo.descripcion,
              imagenUrl: "",
            },
          ]
    );
    closeModal();

    try {
      // 6.1) Gestionar subida de imagen
      let imagenUrl = newGrupo.imagenUrl || "";
      if (newGrupo.imagen && typeof newGrupo.imagen !== "string") {
        const storage = getStorage();
        const imageRef = ref(storage, `grupos/${newGrupo.imagen.name}`);
        const snap = await uploadBytes(imageRef, newGrupo.imagen);
        imagenUrl = await getDownloadURL(snap.ref);
      }

      if (isEdit) {
        // actualización
        const docRef = doc(gruposCol, newGrupo.id);
        await updateDoc(docRef, {
          nombre: newGrupo.nombre,
          descripcion: newGrupo.descripcion || "",
          imagenUrl,
        });
        Swal.fire("Grupo actualizado!", "", "success");
      } else {
        // creación
        const docRef = await addDoc(gruposCol, {
          nombre: newGrupo.nombre,
          descripcion: newGrupo.descripcion || "",
          imagenUrl,
        });
        // reemplazar temporal con el ID real
        setGrupos((prev) =>
          prev.map((g) =>
            g.id === tempId
              ? {
                  id: docRef.id,
                  nombre: newGrupo.nombre,
                  descripcion: newGrupo.descripcion,
                  imagenUrl,
                }
              : g
          )
        );
        Swal.fire("Grupo agregado!", "", "success");
      }
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Hubo un problema guardando el grupo.", "error");
      if (!isEdit) {
        setGrupos((prev) => prev.filter((g) => g.id !== tempId));
      }
    }
  };

  // 7) Eliminar seleccionados
  const handleDeleteSelected = async () => {
    if (!selectedGrupos.length) return;
    const { isConfirmed } = await Swal.fire({
      title: "¿Estás seguro?",
      text: "Se eliminarán los grupos seleccionados.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
    });
    if (!isConfirmed) return;

    try {
      const gruposCol = getSubCollection(ownerUid, "grupos");
      for (let id of selectedGrupos) {
        await deleteDoc(doc(gruposCol, id));
      }
      setGrupos((prev) => prev.filter((g) => !selectedGrupos.includes(g.id)));
      setSelectedGrupos([]);
      Swal.fire("Eliminados!", "", "success");
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "No se pudo eliminar.", "error");
    }
  };

  // 8) Ver detalles
  const handleVerDetalles = (nombre) => navigate(`/grupos/${nombre}/productos`);

  return (
    <>
      <div className="no-sticky">
        <Nav
          handleSearch={setSearchTerm}
          proximasAVencer={proximasAVencer}
          proximosEventos={proximosEventos}
        />

        <div className="task-filters" style={{ flexWrap: "wrap" }}>
          <div
            style={{
              flexBasis: "100%", // ocupa toda la fila
              padding: "0 4px", // mismo gutter que usas
              margin: "0 0 5px", // respirito arriba/abajo
            }}
          >
            <Breadcrumb />
          </div>
          <div className="header-actions-proyectos">
            <button className="action-button-proyectos">
              <i className="bi bi-funnel-fill"></i> Todos
              <i
                className="bi bi-chevron-down"
                style={{ fontSize: "12px" }}
              ></i>
            </button>
            <div className="buscador-pedidos">
              <i className="bi bi-search"></i>
              <input
                type="text"
                className="buscador-input"
                placeholder="Buscar grupos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div className="task-filters-right">
            <button
              className="delete-button"
              style={{
                visibility: selectedGrupos.length ? "visible" : "hidden",
              }}
              onClick={handleDeleteSelected}
            >
              Eliminar
            </button>
            <button className="new-task-button" onClick={() => openModal()}>
              + Nuevo Grupo
            </button>
          </div>
        </div>

        <GrupoModal
          isOpen={isModalOpen}
          onClose={closeModal}
          onSubmit={handleSubmitGrupo}
          grupo={grupoEditando}
        />

        <div className="cotizaciones-table">
          <div className="tabla-cotizaciones-wrapper">
            <div className="tabla-cotizaciones-content">
              {loading ? (
                <Spinner />
              ) : grupos.length === 0 ? (
                <div className="no-resultados">
                  <div
                    className="mensaje-sin-resultados"
                    style={{ marginTop: "30px" }}
                  >
                    <p style={{ fontSize: "14px" }}>Aún no hay grupos.</p>
                  </div>
                  <div className="imagen-sin-resultados">
                    <img
                      src="/img/no-data.svg"
                      alt="Sin grupos"
                      width={350}
                      height={350}
                    />
                  </div>
                </div>
              ) : filteredGrupos.length > 0 ? (
                <table>
                  <thead>
                    <tr>
                      <th>
                        <input
                          type="checkbox"
                          className="checkbox-select-all check"
                          onChange={handleSelectAll}
                          checked={
                            selectedGrupos.length === filteredGrupos.length
                          }
                        />
                      </th>
                      <th>Nombre</th>
                      <th>Descripción</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {page.map((g) => (
                      <tr key={g.id}>
                        <td className="check">
                          <input
                            type="checkbox"
                            className="checkbox"
                            checked={selectedGrupos.includes(g.id)}
                            onChange={() => handleSelectGrupo(g.id)}
                          />
                        </td>
                        <td>{g.nombre}</td>
                        <td>{g.descripcion}</td>
                        <td className="acciones-container">
                          <div className="acciones-icons">
                            <div
                              className="icon-wrapper"
                              onClick={() => handleVerDetalles(g.nombre)}
                            >
                              <i className="bi bi-eye"></i>
                              <span className="tooltipCategorias">
                                Mostrar Detalles
                              </span>
                            </div>
                            <div
                              className="icon-wrapper"
                              onClick={() => openModal(g)}
                            >
                              <i className="bi bi-pencil"></i>
                              <span className="tooltipCategorias">Editar</span>
                            </div>
                            <div className="icon-wrapper">
                              <i className="bi bi-shield-slash"></i>
                              <span className="tooltipCategorias">
                                Desactivar
                              </span>
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="no-resultados">
                  <div
                    className="mensaje-sin-resultados"
                    style={{ marginTop: "30px" }}
                  >
                    <p style={{ fontSize: "14px" }}>
                      No hay grupos que coincidan con tu búsqueda.
                    </p>
                  </div>
                  <div className="imagen-sin-resultados">
                    <img
                      src="/img/noSearch.svg"
                      alt="Sin resultados"
                      className="sin-resultados-imagen"
                    />
                  </div>
                </div>
              )}
            </div>
            {total > 0 && (
              <div className="tabla-cotizaciones-footer">
                <div className="paginador-container">
                  <Paginador
                    totalItems={total}
                    itemsPerPage={itemsPerPage}
                    currentPage={currentPage}
                    onPageChange={setCurrentPage}
                    onItemsPerPageChange={setItemsPerPage}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Grupos;
